import type { PROVIDER_SEARCH_TYPES } from "@/constants";
import { DEFAULT_DISTANCE } from "@/constants";
import type Distance from "@/model/distance";
import type Place from "@/model/place";
import type {
  ProviderSearchCategoryType,
  ProviderSearchDynamicPayloadType,
  ProviderSearchSpecialtyType,
  ProviderSearchState,
  ProviderSearchViewMoreStateType,
} from "@/types";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

const initialState: ProviderSearchState = {
  category: null,
  distance: DEFAULT_DISTANCE,
  location: null,
  network: "IN",
  taxonomyCode: "",
  searchCriteria: "",
  resultPageSearchCriteria: "",
  viewMoreDialog: {
    isVisible: false,
    searchBy: "",
    viewMoreSearchCriteria: "",
  },
  specialty: {
    searchParameter: "",
    specialtyDesc: "",
    type: "",
  },
  specialtyList: [],
  dynamicPayload: {
    category: "",
    searchCriteria: "",
    speciality: "",
    taxonomyCode: "",
    specialityDescription: "",
  },
  searchType: null,
};

const ProviderSearchSlice = createSlice({
  name: "provider-search",
  initialState,
  reducers: {
    setCategory: (
      state,
      action: PayloadAction<ProviderSearchCategoryType | null>,
    ) => {
      state.category = action.payload;
    },
    setDistance: (state, action: PayloadAction<Distance>) => {
      state.distance = action.payload;
    },
    setLocation: (state, action: PayloadAction<Place | null>) => {
      state.location = action.payload;
    },
    setNetwork: (state, action: PayloadAction<string>) => {
      state.network = action.payload;
    },
    setTaxonomyCode: (state, action: PayloadAction<string>) => {
      state.taxonomyCode = action.payload;
    },
    setSearchCriteria: (state, action: PayloadAction<string>) => {
      state.searchCriteria = action.payload;
    },
    setResultPageSearchCriteria: (state, action: PayloadAction<string>) => {
      state.resultPageSearchCriteria = action.payload;
    },
    setViewMoreDialog: (
      state,
      action: PayloadAction<ProviderSearchViewMoreStateType>,
    ) => {
      state.viewMoreDialog = action.payload;
    },
    setSpecialty: (
      state,
      action: PayloadAction<ProviderSearchSpecialtyType>,
    ) => {
      state.specialty = action.payload;
    },
    setSpecialtyList: (
      state,
      action: PayloadAction<ProviderSearchSpecialtyType[]>,
    ) => {
      state.specialtyList = action.payload;
    },
    setProviderSearchDynamicPayload: (
      state,
      action: PayloadAction<ProviderSearchDynamicPayloadType>,
    ) => {
      state.dynamicPayload = action.payload;
    },
    setProviderSearchType: (
      state,
      action: PayloadAction<PROVIDER_SEARCH_TYPES | null>,
    ) => {
      state.searchType = action.payload;
    },
    resetSearchPayload: (state) => {
      state.category = null;
      state.specialty = {
        searchParameter: "",
        specialtyDesc: "",
        type: "",
      };
      state.distance = DEFAULT_DISTANCE;
      state.specialtyList = [];
      state.searchCriteria = "";
      state.taxonomyCode = "";
      state.dynamicPayload = {
        category: "",
        searchCriteria: "",
        speciality: "",
        taxonomyCode: "",
      };
      state.searchType = null;
    },
  },
});

export const {
  setCategory,
  setDistance,
  setLocation,
  setNetwork,
  setTaxonomyCode,
  setSearchCriteria,
  setResultPageSearchCriteria,
  setViewMoreDialog,
  setSpecialty,
  setSpecialtyList,
  setProviderSearchDynamicPayload,
  setProviderSearchType,
  resetSearchPayload,
} = ProviderSearchSlice.actions;

export default ProviderSearchSlice.reducer;
